// homeSummaryCompose.js — THE ONE SOURCE of the summary at the top of the home
// (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1; Nika's pick 2026-09-28 row 2: the Brief
// OR the Signals, a personal option).
//
// Both summary styles and the greeting sub-line render from the model this
// function returns, and nothing else:
//
//   { source, status, line, tiles, brief, briefChips, updatedAt }
//     source     'signals' today. The server AI source (ai-boards, a later
//                current-23 core lane) will answer the SAME shape, so it
//                replaces this function without a change to any component.
//     status     'waiting' — the providers are still answering: the surfaces
//                hold their skeleton, never a premature "nothing";
//                'nothing' — every installed provider answered and none
//                emitted a signal: "Nothing needs you right now";
//                'ready'.
//     line       the greeting sub-line: the top two signals (joined by " · ").
//     tiles      the Signals style: the top four signals.
//     brief      the Brief style: at most two sentences, each an array of runs
//                — { key, text } words, { key, chip } a signal (its count and
//                words, a link to its list), { key, time } the nearest start.
//     briefChips the AI sentence's signals: their own row below it.
//     updatedAt  when the last answer landed (the Brief's "Updated").
//
// THE TEMPLATES ARE DETERMINISTIC (no AI until the AI source is wired — and
// nothing is labelled "AI"): every number on screen is a signal's own count or
// its own `at`. Nothing is summed, nothing is estimated, no "and N more":
//   1. the signals that need attention (urgent + attention), at most three:
//      "Waiting on you: {facts}."; with none, the rest: "You have {facts}.";
//   2. then, when the first sentence named the attention group, the rest, at
//      most three: "You also have {facts}.".
// A fact with a start later today carries it: "3 events today from 10:00".
// The list words are the bundle's own ("{a} and {b}", "{a}, {b} and {c}") —
// never a joiner stitched in code, never Intl.ListFormat (Chrome ships no
// Georgian list data).
//
// FRANK: self-contained exports; siblings through `this.call`.

export const homeSummaryCompose = function homeSummaryCompose(root) {
  const el = this
  const view =
    el && typeof el.call === 'function'
      ? el.call('homeSignalsView', root)
      : { list: [], settled: false, waiting: true, updatedAt: 0 }
  const list = (view && Array.isArray(view.list) ? view.list : []).slice()
  // 'unavailable' — every provider answered with zero items, but an installed
  // app will not come (the loader refused it or failed it: homeSignals.js
  // `failed`). Its items are unknown, so the home never says "nothing" then;
  // it says "Some apps couldn't load" (the loader's own words,
  // workspace.activation.partialTitle). "Nothing" is only for apps that are
  // ALL known, loaded and empty.
  const status = view.waiting
    ? 'waiting'
    : list.length
      ? 'ready'
      : view.settled
        ? view.failed
          ? 'unavailable'
          : 'nothing'
        : 'waiting'
  // THE AI SOURCE (functions/homeSummaryAi.js — ai.boards.summary): the
  // Brief alone switches. While the ask is in flight (≤ 800 ms) the Brief holds
  // its skeleton (`briefStatus` 'waiting'); an answer in time replaces the
  // template's sentences; anything else keeps the template. The greeting line
  // and the Signals tiles never wait for it.
  const fns = el && el.context && el.context.functions
  const ai =
    status === 'ready' && fns && typeof fns.homeSummaryAiState === 'function'
      ? el.call('homeSummaryAiState', root, list)
      : null
  const aiReady = !!(ai && ai.status === 'ready')
  return {
    source: aiReady ? 'ai' : 'signals',
    status,
    briefStatus: ai && ai.status === 'loading' ? 'waiting' : status,
    line: status === 'ready' ? list.slice(0, 2) : [],
    tiles: status === 'ready' ? list.slice(0, 4) : [],
    brief:
      status !== 'ready' || !el
        ? []
        : aiReady
          ? el.call('homeSummaryAiBrief', ai, list)
          : el.call('homeSummaryBrief', list),
    // The AI sentence's signals, as their own row under it (the template's
    // facts are words of its sentences, so it has none).
    briefChips:
      status === 'ready' && el && aiReady ? el.call('homeSummaryAiChips', ai, list) || [] : [],
    // No time while the summary waits: the Updated line belongs to a summary
    // that is on screen (a zero or a mid-load time never reaches the page —
    // "Updated 04:00 AM" was a zero formatted in Tbilisi).
    updatedAt:
      status === 'waiting'
        ? 0
        : aiReady && ai.generatedAt
          ? ai.generatedAt
          : (view && view.updatedAt) || 0
  }
}

// ONE VOICE at the top of the home (qa round 9 on dev, 2026-09-29: the red
// sub-line under the greeting repeated, word for word, what the summary under
// it said — in both styles). Does the summary speak for the signals: the
// Brief's sentences, the Signals tiles, or the skeleton of either while the
// providers (or the AI source) answer? Both styles do in every state but
// 'nothing' (every provider answered empty) and 'unavailable' (empty, but an
// installed app will not come). There the Signals style shows its own calm
// tile (HomeSignalNone: "Nothing needs you right now" or "Some apps couldn't
// load"), and the Brief keeps only its Updated · Refresh row, so the sub-line
// says it. The greeting's
// sub-line (components/HomeTopSummary.js HomeSignalsLine) shows exactly when
// this is false, so the same words are never on screen twice.
export const homeSummaryShowsSignals = function homeSummaryShowsSignals(root) {
  const el = this
  if (!el || typeof el.call !== 'function') return false
  const status = (el.call('homeSummaryCompose', root) || {}).status
  if (status === 'waiting' || status === 'ready') return true
  if (status !== 'nothing' && status !== 'unavailable') return false
  return (el.call('homeLookRead', root) || {}).summaryStyle === 'signals'
}

// THE AI HOLDS THE MAIN COLUMN (Nika 2026-09-29, 23:1x: "when thread is open,
// you can hide top (welcome, summary) part"). While the main thread is open
// (`root._homeThreadOpen`, functions/assistantMainThread.js), a generated
// board takes the view (functions/assistantBoard.js `assistantBoardShown`) or
// a Presentation deck does (functions/assistantDeck.js `assistantDeckShown`),
// the greeting head — the date, the welcome, the sub-line, the Brief /
// Signals — steps aside (pages/main.js HomeGreeting `hide`). The App strip,
// the saved-boards row and the thread chips stay. It reads the flags only: the
// view gates from the registry, so a surface without the assistant (a test, a
// standalone page) reads false. All are read up front, so a reactive caller
// follows each.
export const homeTopYields = function homeTopYields(root) {
  const el = this
  const r = root && typeof root === 'object' ? root : null
  if (!r) return false
  const thread = r._homeThreadOpen === true
  const fns = el && el.context && el.context.functions
  const shown = fns && fns.assistantBoardShown
  const board = typeof shown === 'function' ? !!shown.call(el, r) : false
  const deckShown = fns && fns.assistantDeckShown
  const deck = typeof deckShown === 'function' ? !!deckShown.call(el, r) : false
  // …and the FLY canvas (functions/assistantFly.js assistantFlyShown).
  const flyShown = fns && fns.assistantFlyShown
  const fly = typeof flyShown === 'function' ? !!flyShown.call(el, r) : false
  // …and a home tab's own screen — Present's start (functions/homeTabStart.js
  // homeTabStartShown) or Build's app builder (functions/homeBuild.js
  // homeBuildShown; the home's tabs, Nika 2026-10-23): the tab's head is the
  // screen's, not the dashboard's.
  const startShown = fns && fns.homeTabStartShown
  const present = typeof startShown === 'function' ? !!startShown.call(el, r, 'present') : false
  const buildShown = fns && fns.homeBuildShown
  const build = typeof buildShown === 'function' ? !!buildShown.call(el, r) : false
  return thread || board || deck || fly || present || build
}

// The glyph of a signal's KIND (HOME_SIGNALS_CONTRACT v1 kinds) for the
// Signals tile (components/HomeTopSummary.js HomeSignalGlyph). One table, the
// icon names of the shell's set — each a solid glyph that reads at 20 px. NOT
// `checkCircle` / `bell`: the served sprite carries them as feather strokes
// with a fill on every path, and they paint as a blob (measured on dev
// 0850742, 2026-09-29). An unknown kind reads as information.
export const homeSignalIcon = function homeSignalIcon(sig) {
  const ICONS = {
    overdue: 'clock',
    dueToday: 'date',
    upcoming: 'dateOutline',
    waiting: 'check',
    new: 'email',
    risk: 'flag'
  }
  const kind = sig && typeof sig.kind === 'string' ? sig.kind : ''
  return Object.prototype.hasOwnProperty.call(ICONS, kind) ? ICONS[kind] : 'sfInfoOval'
}

// A Signals tile's accessible name: the severity in WORDS, then the signal's
// own words — "Urgent: 9 overdue next steps". The dot on the glyph shows the
// same severity by shape; neither leans on colour alone.
export const homeSignalsLabel = function homeSignalsLabel(sig) {
  const el = this
  if (!el || typeof el.call !== 'function') return ''
  const words = String(el.call('homeSignalsText', sig) || '')
  const key =
    sig && sig.severity === 'urgent'
      ? 'workspace.home.signals.urgent'
      : sig && sig.severity === 'attention'
        ? 'workspace.home.signals.attention'
        : null
  if (!key) return words
  const said = el.call('polyglot', key)
  return said && said !== key ? said + ': ' + words : words
}

// The Brief's sentences from an ORDERED signal list (see the header).
// `now` is a test seam.
export const homeSummaryBrief = function homeSummaryBrief(list, now) {
  const el = this
  const t = (key) =>
    el && typeof el.call === 'function' ? String(el.call('polyglot', key) ?? key) : key
  const signals = Array.isArray(list) ? list : []
  const attention = signals.filter((s) => s && s.severity !== 'info').slice(0, 3)
  const rest = signals.filter((s) => s && s.severity === 'info').slice(0, 3)
  const nowMs = typeof now === 'number' ? now : Date.now()
  const endOfDay = new Date(nowMs)
  endOfDay.setHours(24, 0, 0, 0)
  // Split a bundle template into literal runs and its `{name}` slots.
  const parts = (template) =>
    String(template)
      .split(/(\{[a-z]+\})/)
      .filter(Boolean)
  const facts = (group, prefix) => {
    const listKey =
      group.length === 3
        ? 'workspace.home.summary.three'
        : group.length === 2
          ? 'workspace.home.summary.two'
          : null
    const template = listKey ? t(listKey) : '{a}'
    const slot = { '{a}': 0, '{b}': 1, '{c}': 2 }
    const runs = []
    parts(template).forEach((part, i) => {
      const at = slot[part]
      if (at === undefined) {
        runs.push({ key: prefix + 'w' + i, text: part })
        return
      }
      const sig = group[at]
      if (!sig) return
      runs.push({ key: prefix + 'c' + at, chip: sig })
      const start = sig.at ? new Date(sig.at).getTime() : NaN
      if (Number.isFinite(start) && start >= nowMs && start < endOfDay.getTime()) {
        const time =
          el && typeof el.call === 'function'
            ? el.call('localizedDate', start, { hour: '2-digit', minute: '2-digit' })
            : ''
        if (time) {
          runs.push({ key: prefix + 't' + at, text: ' ' })
          runs.push({
            key: prefix + 'f' + at,
            time: t('workspace.home.brief.from').replace('{time}', time)
          })
        }
      }
    })
    return runs
  }
  const sentence = (key, group, prefix) => {
    const runs = []
    parts(t(key)).forEach((part, i) => {
      if (part === '{facts}') runs.push(...facts(group, prefix))
      else runs.push({ key: prefix + 's' + i, text: part })
    })
    return runs
  }
  const out = []
  if (attention.length) {
    out.push(sentence('workspace.home.brief.waiting', attention, 'a'))
    if (rest.length) out.push(sentence('workspace.home.brief.also', rest, 'b'))
  } else if (rest.length) {
    out.push(sentence('workspace.home.summary.sentence', rest, 'a'))
  }
  return out
}

// "Open as board" — the Brief's third action: the assistant builds a board of
// the same signals in Page mode (functions/assistantBoard.js
// assistantBoardGenerate). The prompt names the signals in the person's
// language and carries no number a signal did not return. Offered only while
// Page mode can show a board on the home (assistantBoardPageOnHome).
export const homeSummaryOpenAsBoard = function homeSummaryOpenAsBoard() {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || typeof el.call !== 'function') return null
  const model = el.call('homeSummaryCompose', root)
  const words = (model.tiles || []).map((s) => el.call('homeSignalsText', s)).filter(Boolean)
  if (!words.length) return null
  const template = String(el.call('polyglot', 'workspace.home.brief.boardPrompt') ?? '')
  const prompt = template.replace('{facts}', words.join(' · '))
  return el.call('assistantBoardGenerate', prompt)
}
